/**
 * Calendar navigation in the rail (plan §3.1): mini month, "My calendars" (the default calendar and
 * additional own ones: new, share, name and colour, delete), "Other calendars" (colleagues, rooms, equipment; colour,
 * remove) and "Add calendar". Each calendar offers its actions in a "…" menu and on right-click (one registry).
 */
import { useI18n } from '@platform/i18n';
import { ActionOverflowMenu, addDays, Badge, Button, PromptDialog, CALENDAR_COLOR_CLASSES, Checkbox, cn, CalendarToggle, CONTEXT_ID_ATTRIBUTE, ContextMenuArea, ErrorState, MiniMonth, resolveActions, Skeleton, toast, useErrorText } from '@platform/ui';
import { CalendarCheckIcon, EyeIcon, CakeIcon, CalendarPlusIcon, DoorOpenIcon, GlobeIcon, MailCheckIcon, PartyPopperIcon, PlusIcon, ProjectorIcon, TriangleAlertIcon, UserIcon } from 'lucide-react';
import { useState } from 'react';
import { useBookingRequests } from './booking-hooks';
import { createCalendarListActions, isExtraCalendar } from '../actions/calendar-actions';
import { api, type CalendarInfo } from '../api';
import { useCalendar } from './calendar-context';
import { calendarName } from './events';
import { useCalendarFormat, viewRange } from './format';
import { startDownload } from '../download';
import { groupCalendar, moveCalendar, railSections, sectionOf } from './layout';

/** Saves a calendar as .ics: the last 30 days and the next year (Outlook "Save calendar" with a date range). */
function downloadCalendar(calendarId: string): void {
  const now = Date.now();
  startDownload(api.calendarIcsUrl(calendarId, new Date(now - 30 * 86_400_000).toISOString(), new Date(now + 365 * 86_400_000).toISOString()));
}

const KIND_ICON = { own: undefined, shared: <UserIcon />, room: <DoorOpenIcon />, equipment: <ProjectorIcon />, subscription: <GlobeIcon />, holiday: <PartyPopperIcon />, birthday: <CakeIcon /> } as const;

export function CalendarRail() {
  const { t, formatNumber } = useI18n();
  const format = useCalendarFormat();
  const errorText = useErrorText();
  const { date, setDate, view, calendars, visible, toggleCalendar, setDialog, changed, version, categories, categoryFilter, toggleCategoryFilter, settings, layout, saveLayout, showOnly, showAll, zone } = useCalendar();
  // Booking requests of resources the user approves (delegates only; the entry is hidden for everybody else).
  const bookingRequests = useBookingRequests(version);
  const pending = (bookingRequests.data ?? []).filter((r) => r.status === 'pending').length;
  const range = viewRange(view, date, format.weekStart, settings.customDays);
  const list = calendars.data ?? [];
  const sections = railSections(list, layout);
  const own = sections.own;
  const others = sections.other;
  const [grouping, setGrouping] = useState<CalendarInfo>();

  const note = (calendar: CalendarInfo): string | undefined => {
    if (calendar.access === 'none') return t('webmail.calendar.rail.noAccess');
    if (calendar.access === 'freebusy') return t('webmail.calendar.rail.freeBusyOnly');
    if (calendar.access === 'details') return t('webmail.calendar.rail.titlesOnly');
    if (calendar.access === 'reviewer') return t('webmail.calendar.rail.readOnly');
    if (calendar.access === 'contributor') return t('webmail.calendar.rail.createOnly');
    if (calendar.access === 'author') return t('webmail.calendar.rail.ownItemsOnly');
    return undefined;
  };

  const actions = createCalendarListActions({
        share: (calendar) => setDialog({ kind: 'sharing', calendar }),
        publish: (calendar) => setDialog({ kind: 'publish', calendar }),
        importIcs: (calendar) => setDialog({ kind: 'import', calendar }),
        exportIcs: (calendar) => downloadCalendar(calendar.id),
        refresh: async (calendar) => {
          try {
            await api.refreshSubscription(calendar.id.slice(2));
            changed();
          } catch (error) {
            errorText.notify(error);
          }
        },
        move: (calendar, direction) => saveLayout(moveCalendar(list, layout, calendar.id, direction)),
        group: (calendar) => setGrouping(calendar),
        ungroup: (calendar) => saveLayout(groupCalendar(layout, calendar.id, '', newGroupId)),
        showOnly: (calendar) => showOnly(calendar.id),
        edit: (calendar) => setDialog({ kind: 'rename', calendar }),
        remove: async (calendar) => {
          try {
            await api.removeCalendar(calendar.id);
            toast.success(t(isExtraCalendar(calendar) ? 'webmail.calendar.list.deletedToast' : 'webmail.calendar.rail.removedToast', { name: calendar.name }));
            changed();
          } catch (error) {
            errorText.notify(error);
          }
        },
      }, {
        canMove: (calendar, direction) => {
          const section = sectionOf(sections, calendar.id);
          const index = section.findIndex((c) => c.id === calendar.id);
          return index >= 0 && Boolean(section[index + direction]);
        },
        grouped: (calendar) => layout.groups.some((g) => g.calendarIds.includes(calendar.id)),
      });
  const menuFor = (id: string) => {
    const calendar = list.find((c) => c.id === id);
    return calendar && { title: calendarName(calendar, t), actions: resolveActions(actions, [calendar], 'context') };
  };

  const row = (calendar: CalendarInfo) => (
    <div key={calendar.id} {...{ [CONTEXT_ID_ATTRIBUTE]: calendar.id }}>
      <CalendarToggle
        name={calendarName(calendar, t)}
        color={calendar.color}
        icon={calendar.access === 'none' ? <TriangleAlertIcon /> : KIND_ICON[calendar.kind]}
        note={note(calendar)}
        checked={visible.has(calendar.id)}
        onCheckedChange={() => toggleCalendar(calendar.id)}
        actions={
          <ActionOverflowMenu
            actions={resolveActions(actions, [calendar], 'overflow')}
            label={t('webmail.calendar.list.actionsOf', { name: calendarName(calendar, t) })}
            title={calendarName(calendar, t)}
            size="icon-sm"
            className="opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100"
          />
        }
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-3 p-2">
      <MiniMonth
        selected={date}
        rangeStart={view === 'month' ? undefined : range.start}
        rangeEnd={view === 'month' ? undefined : range.end}
        now={zone.toWall(new Date())}
        weekStartsOn={format.weekStart}
        weekNumbers={{
          heading: t('ui.dateTime.weekHeading'),
          label: (row: Date) => formatNumber(format.weekInfo(addDays(row, 3)).week),
          accessibleLabel: (row: Date) => { const week = format.weekInfo(addDays(row, 3)); return t('ui.dateTime.weekLabel', { week: formatNumber(week.week), year: formatNumber(week.year, { useGrouping: false }) }); },
        }}
        onSelect={setDate}
        formatMonth={format.month}
        formatWeekdayShort={format.weekdayNarrow}
        formatDayNumber={format.dayNumber}
        formatDayLabel={format.dayLabel}
        labels={{ previousMonth: t('webmail.calendar.previousMonth'), nextMonth: t('webmail.calendar.nextMonth') }}
      />
      {calendars.error ? (
        <ErrorState description={errorText.describe(calendars.error)} onRetry={calendars.reload} />
      ) : calendars.loading && !calendars.data ? (
        <div className="flex flex-col gap-2 p-2" aria-busy="true">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : (
        <ContextMenuArea menuFor={menuFor}>
          <section aria-labelledby="cal-mine" className="flex flex-col">
            <h3 id="cal-mine" className="px-2 pb-1 text-xs font-semibold text-muted-foreground uppercase">
              {t('webmail.calendar.rail.myCalendars')}
            </h3>
            {own.map(row)}
            <Button variant="ghost" size="sm" className="mt-1 justify-start" onClick={() => setDialog({ kind: 'create' })}>
              <PlusIcon />
              {t('webmail.calendar.list.new')}
            </Button>
          </section>
          {sections.groups.map((group) => (
            <section key={group.id} aria-label={group.name} className="mt-3 flex flex-col">
              <h3 className="px-2 pb-1 text-xs font-semibold text-muted-foreground uppercase">{group.name}</h3>
              {group.calendars.map(row)}
            </section>
          ))}
          <section aria-labelledby="cal-other" className="mt-3 flex flex-col">
            <h3 id="cal-other" className="px-2 pb-1 text-xs font-semibold text-muted-foreground uppercase">
              {t('webmail.calendar.rail.otherCalendars')}
            </h3>
            {others.length === 0 && <p className="px-2 text-xs text-muted-foreground">{t('webmail.calendar.rail.noOtherCalendars')}</p>}
            {others.map(row)}
            <Button variant="ghost" size="sm" className="mt-1 justify-start" onClick={() => setDialog({ kind: 'add' })}>
              <CalendarPlusIcon />
              {t('webmail.calendar.add.open')}
            </Button>
          </section>
          {visible.size < list.length && (
            <Button variant="ghost" size="sm" className="mt-1 justify-start" onClick={showAll}>
              <EyeIcon />
              {t('webmail.calendarUx.groups.showAll')}
            </Button>
          )}
          {categories.length > 0 && (
            <section aria-labelledby="cal-categories" className="mt-3 flex flex-col gap-1">
              <h3 id="cal-categories" className="px-2 pb-1 text-xs font-semibold text-muted-foreground uppercase" title={t('webmail.calendarUx.categorize.filterHint')}>
                {t('webmail.calendarUx.categorize.rail')}
              </h3>
              {categories.map((c) => (
                <label key={c.name} className="flex items-center gap-2 rounded-sm px-2 py-1 text-sm hover:bg-accent">
                  <Checkbox checked={categoryFilter.has(c.name)} onCheckedChange={() => toggleCategoryFilter(c.name)} />
                  <span aria-hidden className={cn('size-2.5 rounded-full', CALENDAR_COLOR_CLASSES[c.color].swatch)} />
                  <span className="truncate">{c.name}</span>
                </label>
              ))}
            </section>
          )}
        </ContextMenuArea>
      )}
      <PromptDialog
        open={grouping !== undefined}
        onOpenChange={(open) => !open && setGrouping(undefined)}
        title={t('webmail.calendarUx.groups.moveTo')}
        label={t('webmail.calendarUx.groups.name')}
        requiredMessage={t('webmail.calendarUx.groups.nameRequired')}
        confirmLabel={t('webmail.calendarUx.groups.move')}
        initialValue={layout.groups.find((g) => grouping && g.calendarIds.includes(grouping.id))?.name ?? layout.groups[0]?.name ?? ''}
        onSubmit={(name) => {
          if (grouping) saveLayout(groupCalendar(layout, grouping.id, name, newGroupId));
          setGrouping(undefined);
        }}
      />
      <section aria-label={t('webmail.calendar.booking.railLabel')} className="flex flex-col border-t pt-2">
        {(bookingRequests.data ?? []).length > 0 && (
          <Button variant="ghost" size="sm" className="justify-start" onClick={() => setDialog({ kind: 'bookingRequests' })}>
            <CalendarCheckIcon />
            <span className="flex-1 text-left">{t('webmail.calendar.booking.title')}</span>
            {pending > 0 && <Badge variant="warning" aria-label={t('webmail.calendar.booking.pendingCount', { n: pending })}>{formatNumber(pending)}</Badge>}
          </Button>
        )}
        <Button variant="ghost" size="sm" className="justify-start" onClick={() => setDialog({ kind: 'invitations' })}>
          <MailCheckIcon />
          {t('webmail.calendar.invitations.open')}
        </Button>
      </section>
    </div>
  );
}

const newGroupId = () => Math.random().toString(36).slice(2, 12);
